import { RegistrationFieldProps } from '../../RegistrationField.types';
import React, {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Box,
  TextInput,
  Text,
  useBreakpoint,
  Breakpoint,
  Input,
} from '@bonanza-social/moonlight';
import { BottomSheet, SelectableOptionItem } from '@bonanza-social/navigation';
import { Platform, Pressable } from 'react-native';
import { selectRegistrationFormData } from '../../../../state';
import { useSelector } from '@bonanza-social/toolkit/state';
import { loadOccupationIndustryOptions } from '../../../../utils/loadOccupationIndustryOptions/loadOccupationIndustryOptions';

type FieldProps = RegistrationFieldProps<'occupation'>;

interface OccupationIndustryOption extends SelectableOptionItem {
  occupation: string;
  industry: string;
}

function OccupationIndustryField(
  {
    onValue,
    value,
    placeholderNS,
    placeholderKey,
    headerKey,
    headerNS,
    error,
  }: FieldProps,
  _: ForwardedRef<TextInput>,
) {
  const occupationInputRef = useRef<TextInput>(null);
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === Breakpoint.DESKTOP;
  const snapPoints = useMemo(
    () => (isDesktop ? ['75%'] : ['100%']),
    [isDesktop],
  );
  const maxHeight = useMemo(() => (isDesktop ? '50%' : '65%'), [isDesktop]);

  const onOccupationPressIn = useCallback(async () => {
    const options = await loadOccupationIndustryOptions();
    BottomSheet.open({
      namespace: 'common',
      type: 'InputSelect',
      detached: Platform.select({ ios: false, web: true, android: false }),
      snapPoints: snapPoints,
      enableDynamicSizing: false,
      contentPaddingHorizontal: 'space-0',
      data: {
        selectableOptions: options,
        headerNS: 'registration',
        headerKey: 'occupation',
        buttonNS: 'registration',
        buttonKey: 'saveButton',
        centerContent: true,
        maxHeight: maxHeight,
        defaultValue: value,
        value,
        onSelect: (selected: OccupationIndustryOption) => {
          onValue('', {
            occupation: selected?.occupation ?? '',
            industry: selected?.industry,
          });
        },
      },
    });
  }, [value, onValue, maxHeight, snapPoints]);

  const formData = useSelector(selectRegistrationFormData);
  const displayValue = useMemo(() => {
    if (formData.occupation && formData.industry) {
      if (formData.occupation === formData.industry) {
        return formData.occupation;
      }
      return `${formData.occupation} - ${formData.industry}`;
    }
    return formData.occupation || '';
  }, [formData.occupation, formData.industry]);

  return (
    <Box>
      <Box flexDirection="row" gap="space-200" alignItems="center">
        <Box flex={1}>
          <Text
            color="darkSurfacePrimary"
            marginBottom="space-300"
            variant="h5"
            locNS={headerNS}
            locKey={headerKey}
          />
          <Pressable onPress={onOccupationPressIn}>
            <Input
              ref={occupationInputRef}
              value={displayValue}
              placeholderNS={placeholderNS}
              placeholderKey={placeholderKey}
              error={error}
              editable={false}
            />
          </Pressable>
        </Box>
      </Box>
      <Text
        variant="body-s"
        color="darkSurfaceSecondary"
        locNS="registration"
        locKey="occupationFooter"
        marginTop="space-200"
      />
    </Box>
  );
}

export default memo(forwardRef<TextInput, FieldProps>(OccupationIndustryField));
